import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { clientApi, setClientToken, errMsg } from "@/lib/api";
import { applyBrandColor } from "@/lib/theme";
import { applyTenantManifest } from "@/lib/pwa";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, LogIn, CalendarDays, KeyRound, HelpCircle } from "lucide-react";
import InstallBanner from "@/components/InstallBanner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientLogin() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const slug = sp.get("slug") || localStorage.getItem("last_booking_slug") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const [forceChange, setForceChange] = useState(null); // {client, token}
  const [np1, setNp1] = useState("");
  const [np2, setNp2] = useState("");

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/api/public/${slug}/settings`).then(({ data }) => {
      setBrand(data || {});
      applyBrandColor(data?.brand_color || "");
      applyTenantManifest(slug, data?.pwa_icon_url || "");
    }).catch(() => {});
  }, [slug]);

  const finishLogin = (client, token) => {
    setClientToken(token);
    localStorage.setItem("client_user", JSON.stringify(client));
    toast.success(`Olá, ${client.name}`);
    nav(slug ? `/agendar/${slug}` : "/agendar", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await clientApi.post("/client/login", { email, password });
      if (data.client?.password_must_change) {
        // Stage the token so the forced-change dialog can call the change endpoint.
        setForceChange({ client: data.client, token: data.token });
      } else {
        finishLogin(data.client, data.token);
      }
    } catch (err) { toast.error(errMsg(err, "Falha no login")); }
    finally { setLoading(false); }
  };

  const submitForcedChange = async () => {
    if (!np1 || np1.length < 6) { toast.error("Nova senha deve ter ao menos 6 caracteres"); return; }
    if (np1 !== np2) { toast.error("As senhas não conferem"); return; }
    try {
      // Use the just-issued client token to call the change endpoint.
      await axios.post(`${API}/api/client/change-password`, { new_password: np1 }, {
        headers: { Authorization: `Bearer ${forceChange.token}` },
      });
      const client = { ...forceChange.client };
      delete client.password_must_change;
      finishLogin(client, forceChange.token);
    } catch (err) { toast.error(errMsg(err, "Falha ao trocar senha")); }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
      <div className="surface p-8 max-w-md w-full space-y-6">
        {brand.cover_url && (
          <div className="relative rounded-xl overflow-hidden -mx-2" style={{ aspectRatio: "3 / 1" }}>
            <img src={brand.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <div className="absolute inset-0 bg-black/30" />
          </div>
        )}
        <div className="flex items-center gap-3">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.business_name || "Logo"} className="h-11 w-11 rounded-xl object-contain bg-white p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><CalendarDays className="h-5 w-5" /></div>
          )}
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">{brand.business_name || "Entrar para agendar"}</h1>
            <p className="text-xs text-muted-foreground">Acesse sua conta para escolher horário</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="client-login-form">
          <div className="space-y-2"><Label>Email</Label><Input data-testid="cl-email" type="email" required className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div className="space-y-2"><Label>Senha</Label><Input data-testid="cl-password" type="password" required className="h-12" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          <Button data-testid="btn-cl-login" disabled={loading} className="w-full h-12">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />} Entrar
          </Button>
          <button type="button" onClick={() => setHelpOpen(true)} className="w-full text-center text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1" data-testid="link-forgot-pw">
            <HelpCircle className="h-3.5 w-3.5" /> Esqueci minha senha
          </button>
        </form>
        <div className="text-center text-sm text-muted-foreground">
          Novo por aqui? <Link to={slug ? `/agendar/registrar?slug=${slug}` : "/agendar/registrar"} className="underline text-foreground" data-testid="link-cl-register">Criar conta</Link>
        </div>
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent data-testid="dialog-forgot-pw">
          <DialogHeader>
            <DialogTitle>Esqueci minha senha</DialogTitle>
            <DialogDescription>
              Entre em contato com o estabelecimento (WhatsApp ou telefone) e peça uma <strong>senha temporária</strong>. Ele redefine para você em segundos.
              Ao entrar com essa senha, o sistema pede automaticamente que você crie sua senha pessoal.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            {brand.business_name ? `Estabelecimento: ${brand.business_name}` : "Fale com o estabelecimento onde você agenda."}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!forceChange} onOpenChange={(v) => { if (!v) setForceChange(null); }}>
        <DialogContent data-testid="dialog-force-change" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Crie sua senha pessoal</DialogTitle>
            <DialogDescription>
              O estabelecimento definiu uma senha temporária para você. Antes de continuar, escolha uma senha pessoal com pelo menos 6 caracteres.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Nova senha</Label>
              <Input data-testid="input-new-pw" type="password" value={np1} onChange={(e) => setNp1(e.target.value)} autoFocus />
            </div>
            <div className="space-y-2">
              <Label>Confirme a nova senha</Label>
              <Input data-testid="input-new-pw2" type="password" value={np2} onChange={(e) => setNp2(e.target.value)} />
            </div>
            <Button className="w-full" onClick={submitForcedChange} data-testid="btn-confirm-new-pw"><KeyRound className="h-4 w-4 mr-1" /> Salvar e continuar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <InstallBanner appName={brand?.business_name || "Agenda"} brandColor={brand?.brand_color} />
    </div>
  );
}
